/* eslint-disable max-lines */
import * as c from './types';

import {
  NominalSpeed,
  Quality,
  Work,
  WorkOrder,
  WorkOrderExportData,
  WorkOrderTarget
} from './action';

import { BulkCreateError } from 'types';
import createReducer from '../../reducers/utils/createReducer';

type Status = 'idle' | 'loading' | 'success' | 'error';
export interface ISku {
  id: string;
  companyId: number;
  sku: string;
  name: string;
  description: string;
  unit: string;
  Machines:
    | {
        id: number;
        title: string;
        MachineAndSku: { idealSpeedByHour: number; countingFactor: number };
      }[]
    | null;
}

interface ISkuDetails {
  id: string;
  sku: string;
  name: string;
  unit: string;
}

interface RawMaterial {
  id: string;
  name: string;
}

export interface SkuState {
  skuData: ISku[];
  workOrderData: WorkOrder[];
  workOrderTargetData: WorkOrderTarget[];
  workOrderByMachine: WorkOrderTarget[];
  workOrderTargetPaginatedData: { data: WorkOrderTarget[]; totalItems: number };
  nominalSpeedData: NominalSpeed[];
  workData: Work[];
  qualityData: Quality[];
  failedSkuEntries: BulkCreateError[];
  failedWorkOrderEntries: BulkCreateError[];
  failedNominalSpeedEntries: BulkCreateError[];
  failedMachineMappingEntries: BulkCreateError[];
  failedQualityEntries: BulkCreateError[];
  isSkuLoading: boolean;
  isSkuLoaded: boolean;
  isWorkOrderLoading: boolean;
  isWorkOrderByMachineLoading: boolean;
  isWorkOrderTargetLoading: boolean;
  isWorkOrderTargetPaginatedLoading: boolean;
  isWorkLoading: boolean;
  isNominalSpeedLoading: boolean;
  isCreateSkuLoading: boolean;
  isDeleteSkuLoading: boolean;
  isUpdateSkuLoading: boolean;
  isCreateWorkOrderLoading: boolean;
  isDeleteWorkOrderLoading: boolean;
  isUpdateWorkOrderLoading: boolean;
  createNominalSpeedStatus: Status;
  isDeleteNominalSpeedLoading: boolean;
  isBulkDeleteNominalSpeedLoading: boolean;
  isBulkDeleteWorkOrderLoading: boolean;
  isCreateNominalSpeedWithMultipleMachines: boolean;
  updateNominalSpeedStatus: Status;
  isQualitiesLoading: boolean;
  isCreateQualityLoading: boolean;
  isDeleteQualityLoading: boolean;
  isUpdateQualityLoading: boolean;
  error: any;
  newlyCreatedSku: ISkuDetails | null;
  newlyEditedSku: ISku | null;
  isWorkOrderDeleted: boolean;
  isImportWorkOrderLoading: boolean;
  importWorkOrderData: any[];
  importWorkOrderError: any;
  isUpdateMachineMappingLoading: boolean;
  isWorkOrderExportDataLoading: boolean;
  workOrderExportData: WorkOrderExportData;
  rawMaterials: RawMaterial[];
  isRawMaterialsLoading: boolean;
  rawMaterialsError: any;
  isBulkCreateNominalSpeeds: boolean;
}

const initialState: SkuState = {
  skuData: [],
  workOrderData: [],
  workOrderByMachine: [],
  workOrderTargetData: [],
  workOrderTargetPaginatedData: { data: [], totalItems: 0 },
  nominalSpeedData: [],
  workData: [],
  qualityData: [],
  failedSkuEntries: [],
  failedWorkOrderEntries: [],
  failedNominalSpeedEntries: [],
  failedMachineMappingEntries: [],
  failedQualityEntries: [],
  isSkuLoading: false,
  isSkuLoaded: false,
  isWorkOrderLoading: false,
  isWorkOrderByMachineLoading: false,
  isWorkOrderTargetLoading: false,
  isWorkOrderTargetPaginatedLoading: false,
  isWorkLoading: false,
  isNominalSpeedLoading: false,
  isCreateSkuLoading: false,
  isDeleteSkuLoading: false,
  isUpdateSkuLoading: false,
  isCreateWorkOrderLoading: false,
  isDeleteWorkOrderLoading: false,
  isUpdateWorkOrderLoading: false,
  isQualitiesLoading: false,
  isCreateQualityLoading: false,
  isDeleteQualityLoading: false,
  isUpdateQualityLoading: false,
  createNominalSpeedStatus: 'idle',
  isDeleteNominalSpeedLoading: false,
  isBulkDeleteNominalSpeedLoading: false,
  isCreateNominalSpeedWithMultipleMachines: false,
  updateNominalSpeedStatus: 'idle',
  error: null,
  newlyCreatedSku: null,
  newlyEditedSku: null,
  isBulkDeleteWorkOrderLoading: false,
  isWorkOrderDeleted: false,
  isImportWorkOrderLoading: false,
  importWorkOrderData: [],
  importWorkOrderError: null,
  isUpdateMachineMappingLoading: false,
  workOrderExportData: {},
  isWorkOrderExportDataLoading: false,
  rawMaterials: [],
  isRawMaterialsLoading: false,
  rawMaterialsError: null,
  isBulkCreateNominalSpeeds: false
};

export default createReducer(initialState, {
  [c.GET_SKU_REQUEST]: (state = initialState) => ({
    ...state,
    error: null,
    isSkuLoading: true
  }),
  [c.GET_SKU_SUCCESS]: (state = initialState, { payload }) => {
    if (!payload) return state;
    return {
      ...state,
      skuData: payload,
      isSkuLoading: false,
      isSkuLoaded: true
    };
  },
  [c.GET_SKU_ERROR]: (state = initialState, { payload }) => {
    if (!payload) return state;
    return {
      ...state,
      error: payload,
      isSkuLoading: false
    };
  },
  [c.CREATE_SKUS_REQUEST]: (state = initialState) => ({
    ...state,
    error: null,
    isCreateSkuLoading: true,
    failedSkuEntries: [],
    newlyCreatedSku: null
  }),
  [c.CREATE_SKUS_SUCCESS]: (state = initialState, { payload }) => {
    return {
      ...state,
      isCreateSkuLoading: false,
      failedSkuEntries: [],
      newlyCreatedSku: payload[0],
      error: null
    };
  },
  [c.CREATE_SKUS_ERROR]: (state = initialState, { payload }) => {
    if (!payload) return state;
    return {
      ...state,
      error: payload,
      isCreateSkuLoading: false,
      failedSkuEntries: payload?.data?.details ?? [],
      newlyCreatedSku: null
    };
  },
  [c.CREATE_SKU_WITH_NOMINAL_SPEED_SUCCESS]: (
    state = initialState,
    { payload }
  ) => {
    return {
      ...state,
      isCreateSkuLoading: false,
      failedSkuEntries: [],
      newlyCreatedSku: {
        id: payload[0].skuId,
        sku: payload.skuInfo.sku,
        name: payload.skuInfo.name,
        unit: payload.skuInfo.unit
      }
    };
  },
  [c.DELETE_SKU_REQUEST]: (state = initialState) => ({
    ...state,
    error: null,
    isDeleteSkuLoading: true
  }),
  [c.DELETE_SKU_SUCCESS]: (state = initialState) => {
    return {
      ...state,
      isDeleteSkuLoading: false
    };
  },
  [c.DELETE_SKU_ERROR]: (state = initialState, { payload }) => {
    if (!payload) return state;
    return {
      ...state,
      error: payload,
      isDeleteSkuLoading: false
    };
  },
  [c.UPDATE_SKU_REQUEST]: (state = initialState) => ({
    ...state,
    error: null,
    isUpdateSkuLoading: true,
    newlyEditedSku: null
  }),
  [c.UPDATE_SKU_SUCCESS]: (state = initialState, { payload }) => {
    if (!payload) return state;
    return {
      ...state,
      isUpdateSkuLoading: false,
      newlyEditedSku: {
        id: payload.id,
        companyId: -1,
        sku: payload.sku,
        name: payload.name,
        description: '',
        unit: payload.unit,
        Machines: null
      }
    };
  },
  [c.UPDATE_SKU_ERROR]: (state = initialState, { payload }) => {
    if (!payload) return state;
    return {
      ...state,
      error: payload,
      isUpdateSkuLoading: false,
      newlyEditedSku: null
    };
  },
  [c.GET_WORK_ORDER_TARGET_REQUEST]: (state = initialState) => ({
    ...state,
    error: null,
    isWorkOrderTargetLoading: true
  }),
  [c.GET_WORK_ORDER_TARGET_SUCCESS]: (state = initialState, { payload }) => {
    if (!payload) return state;
    return {
      ...state,
      workOrderTargetData: payload,
      isWorkOrderTargetLoading: false
    };
  },
  [c.GET_WORK_ORDER_TARGET_ERROR]: (state = initialState, { payload }) => {
    if (!payload) return state;
    return {
      ...state,
      error: payload,
      isWorkOrderTargetLoading: false
    };
  },
  [c.GET_WORK_ORDER_TARGET_PAGINATED_REQUEST]: (state = initialState) => ({
    ...state,
    error: null,
    isWorkOrderTargetPaginatedLoading: true
  }),
  [c.GET_WORK_ORDER_TARGET_PAGINATED_SUCCESS]: (
    state = initialState,
    { payload }
  ) => {
    if (!payload) return state;
    return {
      ...state,
      workOrderTargetPaginatedData: payload,
      isWorkOrderTargetPaginatedLoading: false
    };
  },
  [c.GET_WORK_ORDER_TARGET_PAGINATED_ERROR]: (
    state = initialState,
    { payload }
  ) => {
    if (!payload) return state;
    return {
      ...state,
      error: payload,
      isWorkOrderTargetPaginatedLoading: false
    };
  },
  [c.CREATE_WORK_ORDERS_REQUEST]: (state = initialState) => ({
    ...state,
    error: null,
    isCreateWorkOrderLoading: true
  }),
  [c.CREATE_WORK_ORDERS_SUCCESS]: (state = initialState) => {
    return {
      ...state,
      isCreateWorkOrderLoading: false
    };
  },
  [c.CREATE_WORK_ORDERS_ERROR]: (state = initialState, { payload }) => {
    if (!payload) return state;
    return {
      ...state,
      error: payload,
      isCreateWorkOrderLoading: false
    };
  },
  [c.DELETE_WORK_ORDER_REQUEST]: (state = initialState) => ({
    ...state,
    error: null,
    isDeleteWorkOrderLoading: true,
    isWorkOrderDeleted: false
  }),
  [c.DELETE_WORK_ORDER_SUCCESS]: (state = initialState) => {
    return {
      ...state,
      isDeleteWorkOrderLoading: false,
      isWorkOrderDeleted: true
    };
  },
  [c.DELETE_WORK_ORDER_ERROR]: (state = initialState, { payload }) => {
    if (!payload) return state;
    return {
      ...state,
      error: payload,
      isDeleteWorkOrderLoading: false,
      isWorkOrderDeleted: true
    };
  },
  [c.BULK_DELETE_WORK_ORDERS_REQUEST]: (state = initialState) => ({
    ...state,
    error: null,
    isBulkDeleteWorkOrderLoading: true,
    isWorkOrderDeleted: false
  }),
  [c.BULK_DELETE_WORK_ORDERS_SUCCESS]: (state = initialState) => {
    return {
      ...state,
      isBulkDeleteWorkOrderLoading: false,
      isWorkOrderDeleted: true
    };
  },
  [c.BULK_DELETE_WORK_ORDERS_ERROR]: (state = initialState, { payload }) => {
    if (!payload) return state;
    return {
      ...state,
      error: payload,
      isBulkDeleteWorkOrderLoading: false,
      isWorkOrderDeleted: true
    };
  },
  [c.UPDATE_WORK_ORDER_REQUEST]: (state = initialState) => ({
    ...state,
    error: null,
    isUpdateWorkOrderLoading: true
  }),
  [c.UPDATE_WORK_ORDER_SUCCESS]: (state = initialState) => {
    return {
      ...state,
      isUpdateWorkOrderLoading: false
    };
  },
  [c.UPDATE_WORK_ORDER_ERROR]: (state = initialState, { payload }) => {
    if (!payload) return state;
    return {
      ...state,
      error: payload,
      isUpdateWorkOrderLoading: false
    };
  },
  [c.GET_WORK_REQUEST]: (state = initialState) => ({
    ...state,
    error: null,
    isWorkLoading: true
  }),
  [c.GET_WORK_SUCCESS]: (state = initialState, { payload }) => {
    if (!payload) return state;
    return {
      ...state,
      workData: payload,
      isWorkLoading: false
    };
  },
  [c.GET_WORK_ERROR]: (state = initialState, { payload }) => {
    if (!payload) return state;
    return {
      ...state,
      error: payload,
      isWorkLoading: false
    };
  },
  [c.RESET_FAILED_SKUS]: (state = initialState) => ({
    ...state,
    failedSkuEntries: []
  }),
  [c.BULK_CREATE_WORK_ORDERS_REQUEST]: (state = initialState) => ({
    ...state,
    error: null,
    isCreateSkuLoading: true,
    failedWorkOrderEntries: []
  }),
  [c.BULK_CREATE_WORK_ORDERS_SUCCESS]: (state = initialState) => {
    return {
      ...state,
      isCreateSkuLoading: false,
      failedWorkOrderEntries: []
    };
  },
  [c.BULK_CREATE_WORK_ORDERS_ERROR]: (state = initialState, { payload }) => ({
    ...state,
    error: payload,
    isCreateSkuLoading: false,
    failedWorkOrderEntries: payload?.data?.details ?? []
  }),
  [c.SET_FAILED_WORK_ORDERS]: (state = initialState, { payload }) => {
    if (!payload) return state;
    return {
      ...state,
      failedWorkOrderEntries: payload.failedWorkOrderEntries
    };
  },
  [c.RESET_FAILED_WORK_ORDERS]: (state = initialState) => ({
    ...state,
    failedWorkOrderEntries: []
  }),
  [c.GET_NOMINAL_SPEED_REQUEST]: (state = initialState) => ({
    ...state,
    error: null,
    isNominalSpeedLoading: true
  }),
  [c.GET_NOMINAL_SPEED_SUCCESS]: (state = initialState, { payload }) => {
    if (!payload) return state;
    return {
      ...state,
      nominalSpeedData: payload,
      isNominalSpeedLoading: false
    };
  },
  [c.GET_NOMINAL_SPEED_ERROR]: (state = initialState, { payload }) => {
    if (!payload) return state;
    return {
      ...state,
      error: payload,
      isNominalSpeedLoading: false
    };
  },
  [c.CREATE_NOMINAL_SPEED_REQUEST]: (state = initialState) => ({
    ...state,
    error: null,
    createNominalSpeedStatus: 'loading' as Status
  }),
  [c.CREATE_NOMINAL_SPEED_SUCCESS]: (state = initialState) => {
    return {
      ...state,
      createNominalSpeedStatus: 'success' as Status
    };
  },
  [c.CREATE_NOMINAL_SPEED_ERROR]: (state = initialState, { payload }) => {
    if (!payload) return state;
    return {
      ...state,
      error: payload,
      createNominalSpeedStatus: 'error' as Status
    };
  },
  [c.DELETE_NOMINAL_SPEED_REQUEST]: (state = initialState) => ({
    ...state,
    error: null,
    isDeleteNominalSpeedLoading: true
  }),
  [c.DELETE_NOMINAL_SPEED_SUCCESS]: (state = initialState) => {
    return {
      ...state,
      isDeleteNominalSpeedLoading: false
    };
  },
  [c.DELETE_NOMINAL_SPEED_ERROR]: (state = initialState, { payload }) => {
    if (!payload) return state;
    return {
      ...state,
      error: payload,
      isDeleteNominalSpeedLoading: false
    };
  },
  [c.UPDATE_NOMINAL_SPEED_REQUEST]: (state = initialState) => ({
    ...state,
    error: null,
    updateNominalSpeedStatus: 'loading' as Status
  }),
  [c.UPDATE_NOMINAL_SPEED_SUCCESS]: (state = initialState) => {
    return {
      ...state,
      updateNominalSpeedStatus: 'success' as Status
    };
  },
  [c.UPDATE_NOMINAL_SPEED_ERROR]: (state = initialState, { payload }) => {
    if (!payload) return state;
    return {
      ...state,
      error: payload,
      updateNominalSpeedStatus: 'error' as Status
    };
  },
  [c.BULK_CREATE_NOMINAL_SPEEDS_REQUEST]: (state = initialState) => ({
    ...state,
    error: null,
    failedNominalSpeedEntries: [],
    isBulkCreateNominalSpeeds: true
  }),
  [c.BULK_CREATE_NOMINAL_SPEEDS_SUCCESS]: (state = initialState) => {
    return {
      ...state,
      failedNominalSpeedEntries: [],
      isBulkCreateNominalSpeeds: false
    };
  },
  [c.BULK_CREATE_NOMINAL_SPEEDS_ERROR]: (state = initialState, { payload }) => {
    if (!payload) return state;
    return {
      ...state,
      error: payload,
      failedNominalSpeedEntries: payload?.data?.details ?? [],
      isBulkCreateNominalSpeeds: false
    };
  },
  [c.RESET_FAILED_NOMINAL_SPEEDS]: (state = initialState) => ({
    ...state,
    failedNominalSpeedEntries: []
  }),
  [c.BULK_DELETE_NOMINAL_SPEEDS_REQUEST]: (state = initialState) => ({
    ...state,
    error: null,
    isBulkDeleteNominalSpeedLoading: true
  }),
  [c.BULK_DELETE_NOMINAL_SPEEDS_SUCCESS]: (state = initialState) => {
    return {
      ...state,
      isBulkDeleteNominalSpeedLoading: false
    };
  },
  [c.BULK_DELETE_NOMINAL_SPEEDS_ERROR]: (state = initialState, { payload }) => {
    if (!payload) return state;
    return {
      ...state,
      error: payload,
      isBulkDeleteNominalSpeedLoading: false
    };
  },
  [c.BULK_UPDATE_MACHINE_MAPPING_REQUEST]: (state = initialState) => ({
    ...state,
    error: null,
    isUpdateMachineMappingLoading: true,
    failedMachineMappingEntries: []
  }),
  [c.BULK_UPDATE_MACHINE_MAPPING_SUCCESS]: (state = initialState) => {
    return {
      ...state,
      failedMachineMappingEntries: [],
      isUpdateMachineMappingLoading: false
    };
  },
  [c.BULK_UPDATE_MACHINE_MAPPING_ERROR]: (
    state = initialState,
    { payload }
  ) => {
    if (!payload) return state;
    return {
      ...state,
      error: payload,
      failedMachineMappingEntries: payload?.data?.details ?? [],
      isUpdateMachineMappingLoading: false
    };
  },
  [c.RESET_FAILED_MACHINE_MAPPINGS]: (state = initialState) => ({
    ...state,
    failedMachineMappingEntries: []
  }),
  [c.GET_WORK_ORDER_BY_MACHINE_REQUEST]: (state = initialState) => ({
    ...state,
    error: null,
    isWorkOrderByMachineLoading: true
  }),
  [c.GET_WORK_ORDER_BY_MACHINE_SUCCESS]: (
    state = initialState,
    { payload }
  ) => {
    if (!payload) return state;
    return {
      ...state,
      workOrderByMachine: payload,
      isWorkOrderByMachineLoading: false
    };
  },
  [c.GET_WORK_ORDER_BY_MACHINE_ERROR]: (state = initialState, { payload }) => {
    if (!payload) return state;
    return {
      ...state,
      error: payload,
      isWorkOrderByMachineLoading: false
    };
  },
  [c.RESET_WORK_ORDER_BY_MACHINE]: (state = initialState) => ({
    ...state,
    workOrderByMachine: []
  }),
  [c.GET_QUALITIES_REQUEST]: (state = initialState) => ({
    ...state,
    error: null,
    isQualitiesLoading: true
  }),
  [c.GET_QUALITIES_SUCCESS]: (state = initialState, { payload }) => {
    if (!payload) return state;
    return {
      ...state,
      qualityData: payload.sort(
        (a: { order: number }, b: { order: number }) => a.order - b.order
      ),
      isQualitiesLoading: false
    };
  },
  [c.GET_QUALITIES_ERROR]: (state = initialState, { payload }) => {
    if (!payload) return state;
    return {
      ...state,
      error: payload,
      isQualitiesLoading: false
    };
  },
  [c.CREATE_QUALITIES_REQUEST]: (state = initialState) => ({
    ...state,
    error: null,
    isCreateQualityLoading: true,
    failedQualityEntries: []
  }),
  [c.CREATE_QUALITIES_SUCCESS]: (state = initialState) => {
    return {
      ...state,
      isCreateQualityLoading: false,
      failedQualityEntries: []
    };
  },
  [c.CREATE_QUALITIES_ERROR]: (state = initialState, { payload }) => {
    if (!payload) return state;
    return {
      ...state,
      error: payload,
      isCreateQualityLoading: false,
      failedQualityEntries: payload?.data?.details ?? []
    };
  },
  [c.DELETE_QUALITIES_REQUEST]: (state = initialState, { payload }) => ({
    ...state,
    error: null,
    qualityData: state.qualityData.filter(
      (e) => !payload.qualityIds.includes(e.id)
    ),
    isDeleteQualityLoading: true
  }),
  [c.DELETE_QUALITIES_SUCCESS]: (state = initialState) => {
    return {
      ...state,
      isDeleteQualityLoading: false
    };
  },
  [c.DELETE_QUALITIES_ERROR]: (state = initialState, { payload }) => {
    if (!payload) return state;
    return {
      ...state,
      error: payload,
      isDeleteQualityLoading: false
    };
  },
  [c.UPDATE_QUALITY_REQUEST]: (state = initialState) => ({
    ...state,
    error: null,
    isUpdateQualityLoading: true
  }),
  [c.UPDATE_QUALITY_SUCCESS]: (state = initialState) => {
    return {
      ...state,
      isUpdateQualityLoading: false
    };
  },
  [c.UPDATE_QUALITY_ERROR]: (state = initialState, { payload }) => {
    if (!payload) return state;
    return {
      ...state,
      error: payload,
      isUpdateQualityLoading: false
    };
  },
  [c.CREATE_NOMINAL_SPEED_WITH_MULTIPLE_MACHINES_REQUEST]: (
    state = initialState
  ) => ({
    ...state,
    error: null,
    isCreateNominalSpeedWithMultipleMachines: true
  }),
  [c.CREATE_NOMINAL_SPEED_WITH_MULTIPLE_MACHINES_SUCCESS]: (
    state = initialState
  ) => {
    return {
      ...state,
      isCreateNominalSpeedWithMultipleMachines: false
    };
  },
  [c.CREATE_NOMINAL_SPEED_WITH_MULTIPLE_MACHINES_ERROR]: (
    state = initialState,
    { payload }
  ) => ({
    ...state,
    error: payload,
    isCreateNominalSpeedWithMultipleMachines: false
  }),
  [c.RESET_IS_WORK_ORDER_DELETED]: (state = initialState) => ({
    ...state,
    isWorkOrderDeleted: false
  }),
  [c.IMPORT_WORK_ORDERS_REQUEST]: (state = initialState) => ({
    ...state,
    importWorkOrderError: null,
    isImportWorkOrderLoading: true,
    importWorkOrderData: []
  }),
  [c.IMPORT_WORK_ORDERS_SUCCESS]: (state = initialState, { payload }) => {
    if (!payload) return state;
    return {
      ...state,
      isImportWorkOrderLoading: false,
      importWorkOrderData: payload
    };
  },
  [c.IMPORT_WORK_ORDERS_ERROR]: (state = initialState, { payload }) => {
    if (!payload) return state;
    return {
      ...state,
      importWorkOrderError: payload.details,
      isImportWorkOrderLoading: false,
      importWorkOrderData: []
    };
  },
  [c.GET_WORK_ORDER_EXPORT_DATA_REQUEST]: (state = initialState) => ({
    ...state,
    error: null,
    isWorkOrderExportDataLoading: true
  }),
  [c.GET_WORK_ORDER_EXPORT_DATA_SUCCESS]: (
    state = initialState,
    { payload }
  ) => {
    if (!payload) return state;
    return {
      ...state,
      workOrderExportData: payload,
      isWorkOrderExportDataLoading: false
    };
  },
  [c.GET_WORK_ORDER_EXPORT_DATA_ERROR]: (state = initialState, { payload }) => {
    if (!payload) return state;
    return {
      ...state,
      error: payload,
      isWorkOrderExportDataLoading: false
    };
  },
  [c.FETCH_RAW_MATERIALS_REQUEST]: (state = initialState) => ({
    ...state,
    rawMaterialsError: null,
    isRawMaterialsLoading: true
  }),
  [c.FETCH_RAW_MATERIALS_SUCCESS]: (state = initialState, { payload }) => {
    if (!payload) return state;
    return {
      ...state,
      rawMaterials: payload,
      isRawMaterialsLoading: false
    };
  },
  [c.FETCH_RAW_MATERIALS_ERROR]: (state = initialState, { payload }) => {
    if (!payload) return state;
    return {
      ...state,
      rawMaterialsError: payload,
      isRawMaterialsLoading: false
    };
  }
});
